import { Injectable } from '@angular/core';
import { ContentService } from '@blk/cwp-ng-framework';
import { BlendPortfolioUtil } from '@ddt-web/blending';
import { ErrorDCR, HoldingType } from '@ddt-web/config';
import { DataUserDataService, PartnerApiService } from '@ddt-web/data-service';
import { PortfolioConstants, WebUserDataConstants } from '@ddt-web/domain';
import { Config, StoreService } from '@ddt-web/store-service';
import { PartnerConstants } from '@ddt-web/user-data';
import * as dayjs from 'dayjs';
import * as get from 'lodash/get';
import * as has from 'lodash/has';
import * as isEmpty from 'lodash/isEmpty';
import * as pick from 'lodash/pick';
import * as set from 'lodash/set';
import { Observable, zip } from 'rxjs';
import { first, map, take } from 'rxjs/operators';

import { EntityFlags, PartnerPortfolioFlagUtils } from '../alerts/utils';
import { PartnerErrorHelper } from './partner-error-helper';
import { PartnerStoreHelper } from './partner-store-helper';

@Injectable({ providedIn: 'root' })
export class PartnerPortfolioDataService {
	/**
	 * This service only deal with fetch and store data from /tools-m/advisor-center
	 */
	constructor(
		private partnerApiService: PartnerApiService,
		private storeService: StoreService,
		private contentService: ContentService,
		private partnerStoreHelper: PartnerStoreHelper,
		private partnerErrorHelper: PartnerErrorHelper,
		private dataUserDataService: DataUserDataService
	) { }

	/**
	 * Fetching a model from an external source and updating the config stores.
	 * If error, the config store is updated with empty array and an error is thrown.
	 *
	 * @param config the config store
	 * @param externalModelIds array of external model ids
	 * @param overrideOnReimport boolean flag to reimport the partner portfolio
	 */
	public fetchExternalModel<T>(
		config: Config<T>, partnerSource: string,
		externalModelIds: string[],
		overrideOnReimport?: boolean
	): void {
		zip(
			...externalModelIds.map(externalModelId => {
				const externalId = externalModelId.replace(PartnerConstants.PARTNER.PARTNER_IDENTIFIER, '');
				return this.partnerApiService.fetchExternalModel(partnerSource, externalId, overrideOnReimport);
			})
		)
			.pipe(
				first(),
				map(responseJson => {
					const validResponseJson = this.getValidPortfolioResponse(responseJson);

					if (!isEmpty(validResponseJson)) {
						const partnerResponse = this.updateExternalPartnerResponseFlags(validResponseJson);
						const responseWithoutAssetClass = this.updateAssetClassSubstitutions(partnerResponse);
						const updatedResponse = this.dataUserDataService.flattenAllDataResponse(responseWithoutAssetClass);

						return updatedResponse;
					} else {
						throw new Error('PARTNER_NO_PORTFOLIO_ID_ERROR');
					}
				})
			).subscribe(updatedResponse => {
				const modelIds = updatedResponse.filter(child => BlendPortfolioUtil.isModel(child))
					.map(child => get(child, 'identifier')).filter(identifier => identifier);

				if (modelIds.length) {
					// Query AC Server if portfolio contains models. iRetire doesn't give us models information. The data needs to be retrieved from AC Server
					this.storeService.fetch(config);
					this.dataUserDataService.fetchPortfolioWithId(modelIds, config);
					this.storeService.selectByIds(config, modelIds)
						.pipe(first(portfolios => get(portfolios, 'ids', []).length))
						.subscribe(portfolios => {
							// Wait for models to be loaded, then update store for partner portfolios
							// Enrich the partner response from iretire server with APCs we got from acServer
							this.enrichExternalResponseWithAPC(updatedResponse, portfolios);
							this.partnerStoreHelper.updateStoreFromExternalResponse([config], updatedResponse, externalModelIds);
						});
				} else {
					this.partnerStoreHelper.updateStoreFromExternalResponse([config], updatedResponse, externalModelIds);
				}
			}, error => {
				// update store with error object to avoid infinite loop of calls & make sure components render (eg new analysis component)
				this.storeService.upsertMany(config, externalModelIds.map(id => this.partnerErrorHelper.createErrorObject(id)));
				this.partnerErrorHelper.handleResponseError(error, this.contentService.get('PARTNER_NO_PORTFOLIO_ID_ERROR', ErrorDCR.PARTNER_NO_PORTFOLIO_ID_ERROR));
			});
	}

	/**
	 * Fetching a model list from an external source and updating the config stores.
	 * If error, the config store is updated with empty array and an error is thrown.
	 *
	 * @param configs the array of config store
	 * @param partnerSource the external source string
	 */
	public fetchExternalModelList<T>(configs: Array<Config<T>>, partnerSource: string): void {
		configs.forEach(config => this.storeService.fetch(config));
		this.partnerApiService.fetchExternalModelList(partnerSource)
			.pipe(take(1))
			.subscribe(
				responseJson => {
					const response = this.getModelListResponse(responseJson, partnerSource);
					this.partnerStoreHelper.updateStoreFromResponseList(configs, response, responseJson);
				},
				error => {
					configs.forEach(config => this.storeService.upsertMany(config, []));
					this.partnerErrorHelper.handleResponseError(error, this.contentService.get('PARTNER_NO_MODEL_LIST_ERROR', ErrorDCR.PARTNER_NO_MODEL_LIST_ERROR));
				}
			);
	}

	/**
	 * Edit or create a new portfolio on the target platform. Export the portfolio to the third party platform.
	 *
	 * @param partnerSource the external source string
	 * @param portfolioId the portfolio id
	 * @param portfolioJsonData the portfolio object json
	 * @param externalModelId the external model id - provided if EDIT
	 */
	public createUpdateExternalModel(partnerSource: string, portfolioId: string, portfolioJsonData?: any, externalModelId?: string): Observable<any> {
		return this.partnerApiService.createUpdateExternalModel(partnerSource, portfolioId, portfolioJsonData, externalModelId).pipe(
			map(responseJson => {
				return {
					success: responseJson.success,
					platformIdentifier: get(responseJson, 'response.externalModelId'),
					validationAlerts: get(responseJson, 'response.modelValidationResult.results', []),
					errors: responseJson.errors
				};
			})
		);
	}

	/**
	 * Filters out invalid responseJson (success false or response null).
	 *
	 * @param {*} responseJson
	 */
	public getValidPortfolioResponse(responseJsonArray: any[]): Array<{}> {
		return responseJsonArray.filter(responseJson => responseJson && responseJson.success && responseJson.response);
	}

	/**
	 * Handle the model list response
	 *
	 * @param reponseJson the model list response
	 * @param partnerSource the partner source (eg envestnet)
	 */
	public getModelListResponse(responseJson: any, partnerSource: string): Array<{}> {
		if (responseJson && responseJson.success && responseJson.response) {
			const response = new Array<any>();
			responseJson.response.forEach(partnerPortfolio => {
				const portfolio = {
					portfolioId: partnerPortfolio.portfolioId,
					uniqueId: PartnerConstants.PARTNER.PARTNER_IDENTIFIER + partnerPortfolio.portfolioId,
					description: partnerPortfolio.name,
					dateLastModified: dayjs(partnerPortfolio.lastModifiedDate).valueOf(),
					subType: WebUserDataConstants.SUBTYPE.PARTNER_MODEL,
					partnerSource: partnerSource.toUpperCase()
				};

				response.push(portfolio);
			});
			return response;
		}
	}

	/**
	 *
	 *
	 * @param {Array<any>} responseJson
	 * @returns {Array<any>}
	 * @memberof PartnerPortfolioDataService
	 */
	public updateExternalPartnerResponseFlags(responseJson: Array<any>): Array<any> {
		return responseJson.filter(portfolio => !!portfolio)
			.map(portfolio => {
				// update flags object if the portfolio requires a reimport
				if (portfolio.errors) {

					const isModelChanged = portfolio.errors.find(err => (err.code === PartnerConstants.MODEL_CHANGED) && (get(err, 'message', '').toLowerCase() === 'yes'));
					const isModelReimport = portfolio.errors.find(err => (err.code === PartnerConstants.MODEL_REIMPORTED) && (get(err, 'message', '').toLowerCase() === 'no'));
					const isReimportNeeded = !!(isModelChanged && isModelReimport);

					set(portfolio.response, 'allData.' + PartnerConstants.PARTNER_PORTFOLIO_FLAGS.SHOULD_REIMPORT, isReimportNeeded);
				}

				// update root flags object if the portfolio has unsupported securities at APM sleeve level
				if (has(portfolio, 'response')) {

					const children = get(portfolio, 'response.children', []);
					const hasProcessedChildren = children.some(child => get(child, 'portfolio.allData.flags.isProcessed'));
					let sleeveHasUnmatchedSecurities = false;

					children.map(child => {
						const unmatchedSecuritiesFlag = get(child, 'portfolio.allData.flags.originContainsUnmatchedSecurities');
						// get childType and if type is '', set the type to APM_SLEEVE (type is set to '' only when a dirty sleeve is fixed)
						const childType = get(child, 'allData.originPlatform.type') === '' ? HoldingType.APM : get(child, 'allData.originPlatform.type');

						// if the child is an APM sleeve and has originContainsUnmatchedSecurities flag true (unsupported securities)
						// set the positionType to INVALID, set unmatchedSecurities flag for root level to true and set originPlatform.type to APM_SLEEVE
						if (childType === HoldingType.APM && unmatchedSecuritiesFlag) {
							set(child, 'allData.positionType', 'INVALID');
							set(child, 'allData.originPlatform.type', childType); // make sure we persist the APM_SLEEVE type for sleeve that was fixed, but it is dirty again
							sleeveHasUnmatchedSecurities = true;
						}
					});

					const isProcessedFlag = get(portfolio, 'response.allData.flags.isProcessed') || hasProcessedChildren;
					set(portfolio, 'response.allData.flags.isProcessed', isProcessedFlag); // set partner portfolio isProcessed to true if it has any processed children (fixed sleeves)
					set(portfolio, 'response.allData.flags.originSleeveContainsUnmatchedSecurities', sleeveHasUnmatchedSecurities);
				}

				return portfolio;
			});
	}

	/**
	 * Getting distinct instances of an asset class
	 * In case of duplicate value : Adding their weights and merging the asset Classes
	 * Delete unsupported Asset Classes from the response children.
	 * Add the details of the deleted asset class as part of
	 * the child substitution under 'substitutedFrom'.
	 *
	 * @param responseJson responseJson
	 */
	private updateAssetClassSubstitutions(responseJson: Array<any>): Array<any> {
		return responseJson.filter(portfolio => !!portfolio && has(portfolio, 'response.children'))
			.map(portfolio => {
				const portfolioFlags: EntityFlags = get(portfolio.response.allData, PartnerConstants.PARTNER_PORTFOLIO_FLAGS.KEY);
				if (PartnerPortfolioFlagUtils.hasAssetClassSubstitutions(portfolioFlags)) {
					const securitiesToRemove = [];
					portfolio.response.children = portfolio.response.children.reduce((uniquePortfolioArray, currentChild) => {
						const indexOfTickerInUniqueArray = uniquePortfolioArray.findIndex(child => get(child, 'allData.identifier') === get(currentChild, 'allData.identifier'));
						if (indexOfTickerInUniqueArray < 0) {
							// If the asset class is not present in the array, pushing the asset class to the array
							uniquePortfolioArray.push(currentChild);
						} else {
							// If the asset class with the current identifier is already present : Adding the weights and changing the position type to Substituted
							set(uniquePortfolioArray[indexOfTickerInUniqueArray], 'allData.positionType', PortfolioConstants.POSITION_TYPE.SUBSTITUTED);
							set(uniquePortfolioArray[indexOfTickerInUniqueArray], 'allData.weight', currentChild.weight + uniquePortfolioArray[indexOfTickerInUniqueArray].weight);
							set(uniquePortfolioArray[indexOfTickerInUniqueArray], 'weight', currentChild.weight + uniquePortfolioArray[indexOfTickerInUniqueArray].weight);
						}

						// Adding SubstitutedFrom field if the MappedFromID exists in the current child and deleting the corresponding Invalid Security
						const mappedFromId = get(currentChild, 'allData.mappedFrom[0]');
						if (mappedFromId) {
							const mappedFromSecurityIndex = portfolio.response.children.findIndex(child => get(child, 'identifier') === mappedFromId);
							const updatedIndexOfTickerInUniqueArray = uniquePortfolioArray
								.findIndex(child => get(child, 'allData.identifier') === get(currentChild, 'allData.identifier'));

							if (!uniquePortfolioArray[updatedIndexOfTickerInUniqueArray]['allData']['substitutedFrom']) {
								uniquePortfolioArray[updatedIndexOfTickerInUniqueArray]['allData']['substitutedFrom'] = {
									assetClass: [],
									invalidSecurities: []
								};
							}
							// Add unsupportedAssetClass details in substitutedFrom of corresponding child
							const securityMappedFrom = pick(get(portfolio, `response.children[${mappedFromSecurityIndex}].allData`),
								['ticker', 'description', 'weight', 'value', 'uniqueId', 'identifier', 'originPlatform']);
							uniquePortfolioArray[updatedIndexOfTickerInUniqueArray]['allData']['substitutedFrom']['assetClass'].push(securityMappedFrom);
							// Delete unsupported asset class from children
							securitiesToRemove.push(mappedFromId);
						}
						return uniquePortfolioArray;
					}, []).filter(child => !securitiesToRemove.includes(get(child, 'identifier')));
				}
				return portfolio;
			});
	}

	private enrichExternalResponseWithAPC(response: any[], modelsDataFromStore: any) {
		response.forEach(node => {
			this.enrichExternalResponseChildrenWithAPC(node, modelsDataFromStore);
		});
	}

	private enrichExternalResponseChildrenWithAPC(node, modelsDataFromStore) {
		if (!!node?.children?.length) {
			node?.children?.forEach(child => {
				this.enrichExternalResponseChildrenWithAPC(child, modelsDataFromStore);
			});
		} else {
			if (node.identifierType === WebUserDataConstants.IDENTIFIER_TYPE.MODEL_ID) {
				// TODO wdsDecom_migrationToAPC (phase 2): confirm on iretire server behaviour - will it return new ids - might require tweaks 
				node.aladdinPortfolioCode = modelsDataFromStore?.entities?.[node.identifier]?.aladdinPortfolioCode;
			}
		}
	}
}
